'use client';

// Runtime imports - functions, classes, values
import { initializeApp, getApps, getApp } from 'firebase/app';
import { getAuth, connectAuthEmulator } from 'firebase/auth';
import { getFirestore, connectFirestoreEmulator } from 'firebase/firestore';
import { getStorage, connectStorageEmulator } from 'firebase/storage';

// Type-only imports - interfaces, types
import type { FirebaseApp } from 'firebase/app';
import type { Auth } from 'firebase/auth';
import type { Firestore } from 'firebase/firestore';
import type { FirebaseStorage } from 'firebase/storage';
import type { Analytics } from 'firebase/analytics';

// ✅ Enhanced type for Firebase configuration
interface FirebaseConfig {
  apiKey: string;
  authDomain: string;
  projectId: string;
  storageBucket: string;
  messagingSenderId: string;
  appId: string;
  measurementId?: string;
}

// ✅ Enhanced configuration validation with better error messages
const createFirebaseConfig = (): FirebaseConfig => {
  const requiredEnvVars = {
    apiKey: process.env.NEXT_PUBLIC_FIREBASE_API_KEY,
    authDomain: process.env.NEXT_PUBLIC_FIREBASE_AUTH_DOMAIN,
    projectId: process.env.NEXT_PUBLIC_FIREBASE_PROJECT_ID,
    storageBucket: process.env.NEXT_PUBLIC_FIREBASE_STORAGE_BUCKET,
    messagingSenderId: process.env.NEXT_PUBLIC_FIREBASE_MESSAGING_SENDER_ID,
    appId: process.env.NEXT_PUBLIC_FIREBASE_APP_ID,
  };

  // ✅ Enhanced validation with specific error messages
  const missingVars = Object.entries(requiredEnvVars)
    .filter(([_, value]) => !value || value.trim() === '')
    .map(([key]) => key);

  if (missingVars.length > 0) {
    const errorMessage = `❌ Missing required Firebase environment variables: ${missingVars.join(', ')}\n` +
      `Please check your .env.local file and ensure all NEXT_PUBLIC_FIREBASE_* variables are set.`;
    console.error(errorMessage);
    throw new Error(errorMessage);
  }

  // ✅ Validate environment values
  if (requiredEnvVars.projectId && !requiredEnvVars.projectId.match(/^[a-z0-9-]+$/)) {
    throw new Error('Invalid Firebase project ID format');
  }

  const config: FirebaseConfig = {
    apiKey: requiredEnvVars.apiKey!,
    authDomain: requiredEnvVars.authDomain!,
    projectId: requiredEnvVars.projectId!,
    storageBucket: requiredEnvVars.storageBucket!,
    messagingSenderId: requiredEnvVars.messagingSenderId!,
    appId: requiredEnvVars.appId!,
  };

  // ✅ Enhanced measurement ID validation
  const measurementId = process.env.NEXT_PUBLIC_FIREBASE_MEASUREMENT_ID;
  if (measurementId && measurementId.trim() !== '') {
    const trimmedId = measurementId.trim();
    // Validate measurement ID format (should start with G-)
    if (!trimmedId.match(/^G-[A-Z0-9]{10}$/)) {
      console.warn(`⚠️ Invalid measurement ID format: ${trimmedId}. Expected format: G-XXXXXXXXXX`);
    }
    config.measurementId = trimmedId;
  } else {
    console.warn('⚠️ NEXT_PUBLIC_FIREBASE_MEASUREMENT_ID not found. Analytics will not be initialized.');
  }

  return config;
};

// ✅ Enhanced initialization state tracking
interface FirebaseState {
  app: FirebaseApp | null;
  auth: Auth | null;
  db: Firestore | null;
  storage: FirebaseStorage | null;
  analytics: Analytics | null;
  initialized: boolean;
  error: Error | null;
  config: FirebaseConfig | null;
}

const firebaseState: FirebaseState = {
  app: null,
  auth: null,
  db: null,
  storage: null,
  analytics: null,
  initialized: false,
  error: null,
  config: null,
};

// ✅ Enhanced emulator connection logic
const connectToEmulators = (auth: Auth, db: Firestore, storage: FirebaseStorage) => {
  if (process.env.NODE_ENV !== 'development' || typeof window === 'undefined') {
    return;
  }

  const useEmulators = process.env.NEXT_PUBLIC_USE_FIREBASE_EMULATORS === 'true';
  if (!useEmulators) {
    return;
  }

  try {
    // Check if Auth emulator is already connected
    const authConfig = (auth as any)?._config || (auth as any)?._delegate?._config;
    if (!authConfig?.emulator) {
      connectAuthEmulator(auth, 'http://localhost:9099', { disableWarnings: true });
      console.log('🔧 Connected to Auth Emulator');
    }

    // Check if Firestore emulator is already connected  
    const dbConfig = (db as any)?._delegate?._databaseId;
    if (dbConfig && !dbConfig.host?.includes('localhost')) {
      connectFirestoreEmulator(db, 'localhost', 8080);
      console.log('🔧 Connected to Firestore Emulator');
    }

    // Check if Storage emulator is already connected
    const storageConfig = (storage as any)?._delegate?._host;
    if (storageConfig && !storageConfig.includes('localhost')) {
      connectStorageEmulator(storage, 'localhost', 9199);
      console.log('🔧 Connected to Storage Emulator');
    }
  } catch (emulatorError) {
    console.warn('⚠️ Emulator connection failed (this is normal if emulators are already connected):', emulatorError);
  }
};

// ✅ Enhanced initialization with better error handling and development mode support
const initializeFirebase = async (): Promise<FirebaseState> => {
  if (firebaseState.initialized) {
    return firebaseState;
  }

  try {
    console.log('🔄 Initializing Firebase...');
    
    const firebaseConfig = createFirebaseConfig();
    firebaseState.config = firebaseConfig;
    
    // ✅ Safe app initialization
    let app: FirebaseApp;
    if (getApps().length === 0) {
      app = initializeApp(firebaseConfig);
      console.log('✅ New Firebase app initialized');
    } else {
      app = getApp();
      console.log('✅ Existing Firebase app retrieved');
    }

    firebaseState.app = app;

    // ✅ Initialize core services with enhanced error handling
    try {
      firebaseState.auth = getAuth(app);
      console.log('✅ Firebase Auth initialized');
    } catch (authError) {
      console.error('❌ Firebase Auth initialization failed:', authError);
      throw new Error('Failed to initialize Firebase Authentication');
    }

    try {
      firebaseState.db = getFirestore(app);
      console.log('✅ Firebase Firestore initialized');
    } catch (dbError) {
      console.error('❌ Firebase Firestore initialization failed:', dbError);
      throw new Error('Failed to initialize Firebase Firestore');
    }

    try {
      firebaseState.storage = getStorage(app);
      console.log('✅ Firebase Storage initialized');
    } catch (storageError) {
      console.error('❌ Firebase Storage initialization failed:', storageError);
      throw new Error('Failed to initialize Firebase Storage');
    }

    // ✅ Connect to emulators if in development mode
    connectToEmulators(firebaseState.auth, firebaseState.db, firebaseState.storage);

    // Analytics is NOT started here. It only starts after the visitor accepts
    // the cookie banner — see src/lib/analytics-consent.ts.
    firebaseState.analytics = null;

    firebaseState.initialized = true;
    firebaseState.error = null;

    // ✅ Enhanced initialization logging
    console.log('✅ Firebase initialization completed successfully:', {
      hasApp: !!firebaseState.app,
      hasAuth: !!firebaseState.auth,
      hasDb: !!firebaseState.db,
      hasStorage: !!firebaseState.storage,
      hasAnalytics: !!firebaseState.analytics,
      projectId: firebaseConfig.projectId,
      measurementId: firebaseConfig.measurementId || 'Not configured',
      environment: process.env.NODE_ENV,
      timestamp: new Date().toISOString(),
    });

  } catch (error) {
    const initError = error instanceof Error ? error : new Error(String(error));
    firebaseState.error = initError;
    firebaseState.initialized = false;
    
    console.error('❌ Firebase initialization failed:', {
      error: initError.message,
      stack: initError.stack,
      timestamp: new Date().toISOString(),
    });
    
    // Don't throw in production to prevent app crashes
    if (process.env.NODE_ENV === 'development') {
      throw initError;
    }
  }

  return firebaseState;
};

// ✅ Initialize Firebase immediately and handle async properly
let initPromise: Promise<FirebaseState>;
let firebase: FirebaseState = firebaseState;

// Initialize Firebase on module load
initPromise = initializeFirebase().then((state) => {
  firebase = state;
  return state;
});

// ✅ FIXED: Enhanced exports with proper typing to prevent null issues
export const app: FirebaseApp = firebase.app!;
export const auth: Auth = firebase.auth!;
export const db: Firestore = firebase.db!;
export const storage: FirebaseStorage = firebase.storage!;
export const analytics: Analytics | null = firebase.analytics;

// ✅ Async getter for waiting for initialization
export const getFirebaseReady = () => initPromise;

// ✅ FIXED: Enhanced getter functions that guarantee non-null returns
export function getFirebaseApp(): FirebaseApp {
  if (!firebase.initialized) {
    // Try to get from current state if available
    const apps = getApps();
    if (apps.length > 0) {
      // ✅ FIXED: Ensure apps[0] is not undefined
      const app = apps[0];
      if (app) {
        return app;
      }
    }
  }
  
  if (!firebase.app) {
    if (firebase.error) {
      throw new Error(`Firebase app not available: ${firebase.error.message}`);
    }
    throw new Error('Firebase app not initialized. Make sure Firebase is properly configured.');
  }
  return firebase.app;
}

export function getFirebaseAuth(): Auth {
  if (!firebase.initialized) {
    // Try to initialize auth if app is available
    const apps = getApps();
    if (apps.length > 0) {
      // ✅ FIXED: Ensure apps[0] is not undefined
      const app = apps[0];
      if (app) {
        return getAuth(app);
      }
    }
  }
  
  if (!firebase.auth) {
    if (firebase.error) {
      throw new Error(`Firebase Auth not available: ${firebase.error.message}`);
    }
    throw new Error('Firebase Auth not initialized. Make sure Firebase is properly configured.');
  }
  return firebase.auth;
}

export function getFirebaseDb(): Firestore {
  if (!firebase.initialized) {
    // Try to initialize db if app is available
    const apps = getApps();
    if (apps.length > 0) {
      // ✅ FIXED: Ensure apps[0] is not undefined
      const app = apps[0];
      if (app) {
        return getFirestore(app);
      }
    }
  }
  
  if (!firebase.db) {
    if (firebase.error) {
      throw new Error(`Firebase Firestore not available: ${firebase.error.message}`);
    }
    throw new Error('Firebase Firestore not initialized. Make sure Firebase is properly configured.');
  }
  return firebase.db;
}

export function getFirebaseStorage(): FirebaseStorage {
  if (!firebase.initialized) {
    // Try to initialize storage if app is available
    const apps = getApps();
    if (apps.length > 0) {
      // ✅ FIXED: Ensure apps[0] is not undefined
      const app = apps[0];
      if (app) {
        return getStorage(app);
      }
    }
  }
  
  if (!firebase.storage) {
    if (firebase.error) {
      throw new Error(`Firebase Storage not available: ${firebase.error.message}`);
    }
    throw new Error('Firebase Storage not initialized. Make sure Firebase is properly configured.');
  }
  return firebase.storage;
}

export function getFirebaseAnalytics(): Analytics | null {
  return firebase.analytics;
}

// Consent-gated analytics start/stop (src/lib/analytics-consent.ts) uses this
// to keep a single source of truth for the active Analytics instance.
export function setFirebaseAnalytics(instance: Analytics | null): void {
  firebaseState.analytics = instance;
}

// ✅ Enhanced safe getters with better error recovery
export function getSafeAuth(): Auth | null {
  try {
    return getFirebaseAuth();
  } catch (error) {
    console.warn('Failed to get Firebase Auth:', error);
    return null;
  }
}

export function getSafeDb(): Firestore | null {
  try {
    return getFirebaseDb();
  } catch (error) {
    console.warn('Failed to get Firebase Firestore:', error);
    return null;
  }
}

export function getSafeStorage(): FirebaseStorage | null {
  try {
    return getFirebaseStorage();
  } catch (error) {
    console.warn('Failed to get Firebase Storage:', error);
    return null;
  }
}

export function getSafeApp(): FirebaseApp | null {
  try {
    return getFirebaseApp();
  } catch (error) {
    console.warn('Failed to get Firebase App:', error);
    return null;
  }
}

// ✅ Enhanced utility functions
export function isFirebaseInitialized(): boolean {
  return firebase.initialized && !firebase.error;
}

export function getFirebaseError(): Error | null {
  return firebase.error;
}

export function getFirebaseState(): Readonly<FirebaseState> {
  return Object.freeze({ ...firebase });
}

export function getFirebaseConfig(): Readonly<FirebaseConfig> | null {
  return firebase.config ? Object.freeze({ ...firebase.config }) : null;
}

// ✅ Enhanced re-initialization function (for testing or error recovery)
export function reinitializeFirebase(): Promise<FirebaseState> {
  firebaseState.initialized = false;
  firebaseState.error = null;
  firebaseState.app = null;
  firebaseState.auth = null;
  firebaseState.db = null;
  firebaseState.storage = null;
  firebaseState.analytics = null;
  firebaseState.config = null;
  
  // Reset the module-level firebase reference
  firebase = firebaseState;
  
  // Create new initialization promise
  initPromise = initializeFirebase().then((state) => {
    firebase = state;
    return state;
  });
  
  return initPromise;
}

// ✅ Type guards for better TypeScript support
export function isValidFirebaseApp(app: any): app is FirebaseApp {
  return app && typeof app === 'object' && 'name' in app && 'options' in app;
}

export function isValidAuth(auth: any): auth is Auth {
  return auth && typeof auth === 'object' && 'app' in auth && 'currentUser' in auth;
}

export function isValidFirestore(db: any): db is Firestore {
  return db && typeof db === 'object' && 'app' in db && '_delegate' in db;
}

export function isValidStorage(storage: any): storage is FirebaseStorage {
  return storage && typeof storage === 'object' && 'app' in storage && '_delegate' in storage;
}

// ✅ Initialize App Check after Firebase is ready (client-side only)
if (typeof window !== 'undefined') {
  initPromise.then(async (state) => {
    if (state.app && state.initialized) {
      try {
        // Dynamic import to avoid SSR issues
        const { initAppCheck } = await import('./firebase-appcheck');
        initAppCheck();
      } catch (appCheckError) {
        console.warn('⚠️ App Check initialization failed:', appCheckError);
      }
    }
  });
}

// ✅ FIXED: Default export that guarantees FirebaseApp type
export default new Proxy({} as FirebaseApp, {
  get(target, prop) {
    const firebaseApp = getSafeApp();
    if (!firebaseApp) {
      throw new Error('Firebase app not available');
    }
    return firebaseApp[prop as keyof FirebaseApp];
  }
});
